/**
 * @component ApproveCorrectionDialog
 * @description
 * Confirmation dialog for accepting a correction proposal.
 *
 * Matches the exact SIB UI mockup:
 * - Large title
 * - Muted descriptive text
 * - Outline + Solid button layout
 * - Centered footer actions
 */

import { Button, Dialog, Portal } from '@chakra-ui/react'
import { useState } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { acceptCorrection } from '@/api/orders.api'

interface ApproveCorrectionDialogProps {
  isOpen: boolean
  onClose: () => void
  orderId: number
  onAccepted?: () => void
}

export function ApproveCorrectionDialog({
  isOpen,
  onClose,
  orderId,
  onAccepted,
}: ApproveCorrectionDialogProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleApprove = async () => {
    setIsLoading(true)
    try {
      await acceptCorrection(orderId)

      toast.success(
        'Korrektur akzeptiert',
        'Die vorgeschlagenen Leistungen wurden verbindlich übernommen.'
      )

      onClose()
      onAccepted?.()
    } catch (err: any) {
      toast.error(
        'Korrektur konnte nicht akzeptiert werden',
        'Bitte führen Sie den Vorgang erneut aus.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={({ open }) => !open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Korrektur verbindlich akzeptieren</Dialog.Title>
              <Dialog.CloseTrigger />
            </Dialog.Header>

            <Dialog.Body>
              Möchten Sie den Korrekturvorschlag verbindlich akzeptieren? Die in der Korrektur
              vorgeschlagenen Leistungen werden anschließend verbindlich bestellt.
            </Dialog.Body>

            <Dialog.Footer>
              <Button variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                variant="solid"
                colorPalette="green"
                onClick={handleApprove}
                loading={isLoading}
              >
                Korrektur akzeptieren
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
